<script setup>
import { dynamicTime } from 'shared/helpers/timeHelper';
import FlowChannelBadge from './FlowChannelBadge.vue';
import FlowHealthChip from './FlowHealthChip.vue';

const props = defineProps({
  flow: { type: Object, required: true },
  folders: { type: Array, default: () => [] },
  isDeleting: { type: Boolean, default: false },
});

const emit = defineEmits(['open', 'delete', 'move']);

const onFolderChange = event => {
  const { value } = event.target;
  event.target.value = props.flow.folder ?? '';
  emit('move', props.flow, value);
};
</script>

<template>
  <div
    class="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between md:gap-3 bg-n-solid-1"
  >
    <div
      role="button"
      tabindex="0"
      class="min-w-0 flex-1 flex flex-col gap-1 cursor-pointer rounded-md outline-none hover:bg-n-alpha-2 focus-visible:ring-2 focus-visible:ring-n-teal-9 -mx-1 px-1 py-1"
      @click="emit('open', flow)"
      @keydown.enter.prevent="emit('open', flow)"
      @keydown.space.prevent="emit('open', flow)"
    >
      <div class="flex items-center gap-2 flex-wrap">
        <span class="font-medium text-n-slate-12 truncate">{{
          flow.name
        }}</span>
        <FlowChannelBadge
          v-for="channel in flow.channels"
          :key="channel"
          :channel="channel"
        />
      </div>
      <div class="flex items-center gap-2 flex-wrap text-xs text-n-slate-11">
        <FlowHealthChip
          :health="flow.health"
          :last-execution-at="flow.last_execution_at"
        />
        <span aria-hidden="true">·</span>
        <span>{{ dynamicTime(flow.updated_at) }}</span>
      </div>
    </div>

    <div class="flex items-center gap-2 self-end md:self-center">
      <select
        class="h-9 rounded-xl border border-n-weak bg-n-solid-1 px-2 text-xs text-n-slate-11 outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        :value="flow.folder ?? ''"
        :aria-label="$t('FLOW_AUTOMATIONS.INDEX.MOVE_TO_FOLDER')"
        @click.stop
        @change="onFolderChange"
      >
        <option value="">{{ $t('FLOW_AUTOMATIONS.INDEX.NO_FOLDER') }}</option>
        <option v-for="option in folders" :key="option" :value="option">
          {{ option }}
        </option>
        <option value="__new__">
          {{ $t('FLOW_AUTOMATIONS.INDEX.NEW_FOLDER') }}
        </option>
      </select>

      <button
        type="button"
        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-9 disabled:pointer-events-none disabled:opacity-40"
        :disabled="isDeleting"
        :title="$t('FLOW_AUTOMATIONS.INDEX.DELETE_LABEL')"
        :aria-label="$t('FLOW_AUTOMATIONS.INDEX.DELETE_LABEL')"
        @click.stop="emit('delete', flow)"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="size-[18px] shrink-0"
          aria-hidden="true"
        >
          <path d="M3 6h18" />
          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
        </svg>
      </button>
    </div>
  </div>
</template>
